import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CogIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { cookieManager, CookiePreferences } from '../utils/cookieManager';

interface CookieConsentBannerProps {
  onAccept?: () => void;
  onDecline?: () => void;
  onCustomize?: () => void;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onAccept,
  onDecline,
  onCustomize
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    performance: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already given consent
    if (!cookieManager.hasConsent()) {
      setIsVisible(true);
    }
    
    // Load current preferences
    setPreferences(cookieManager.getPreferences());
  }, []);

  const handleAcceptAll = () => {
    const allPreferences: CookiePreferences = {
      essential: true,
      analytics: true,
      performance: true,
      marketing: true
    };
    
    cookieManager.updatePreferences(allPreferences);
    cookieManager.setConsent(true);
    setIsVisible(false);
    onAccept?.();
  };

  const handleDeclineAll = () => {
    const minimalPreferences: CookiePreferences = {
      essential: true,
      analytics: false,
      performance: false,
      marketing: false
    };
    
    cookieManager.updatePreferences(minimalPreferences);
    cookieManager.setConsent(true);
    setIsVisible(false);
    onDecline?.();
  };

  const handleSavePreferences = () => {
    cookieManager.updatePreferences(preferences);
    cookieManager.setConsent(true);
    setIsVisible(false);
    setShowSettings(false);
    onCustomize?.();
  };

  const handlePreferenceChange = (key: keyof CookiePreferences, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl"
      >
        <div className="max-w-7xl mx-auto px-4 py-6">
          {!showSettings ? (
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    We Value Your Privacy
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We use cookies to enhance your browsing experience, analyze website traffic, 
                  and provide personalized content. You can customize your preferences or accept all cookies.
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  <CogIcon className="w-4 h-4 mr-2" />
                  Customize
                </button>
                <button
                  onClick={handleDeclineAll}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                >
                  Decline All
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                >
                  Accept All
                </button>
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-6"
            >
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Close settings"
                  aria-label="Close settings"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Cookie Categories */}
              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">Essential Cookies</h4>
                    <div className="w-12 h-6 bg-blue-600 rounded-full flex items-center justify-end pr-1">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    These cookies are necessary for the website to function properly. They cannot be disabled.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-white border border-gray-200 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">Analytics Cookies</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                        className="sr-only"
                        aria-label="Analytics cookies toggle"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                        preferences.analytics ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 mt-1 ${
                          preferences.analytics ? 'translate-x-7' : 'translate-x-1'
                        }`}></div>
                      </div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                  </p>
                </div>

                {/* Performance Cookies */}
                <div className="bg-white border border-gray-200 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">Performance Cookies</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.performance}
                        onChange={(e) => handlePreferenceChange('performance', e.target.checked)}
                        className="sr-only"
                        aria-label="Performance cookies toggle"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                        preferences.performance ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 mt-1 ${
                          preferences.performance ? 'translate-x-7' : 'translate-x-1'
                        }`}></div>
                      </div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Collect information about how you use our website to improve its performance and user experience.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-white border border-gray-200 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">Marketing Cookies</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                        className="sr-only"
                        aria-label="Marketing cookies toggle"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                        preferences.marketing ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 mt-1 ${
                          preferences.marketing ? 'translate-x-7' : 'translate-x-1'
                        }`}></div>
                      </div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Used to track visitors across websites to display relevant and engaging advertisements.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                >
                  Save Preferences
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
